import React from "react";
import { Link } from "gatsby";
import { slug } from "../utils/slug";
import { twMerge } from "tailwind-merge";

const Btn = ({ btnType, btnClr, btnText, btnLink }) => {
  const cls = `min-w-[187px] h-11 text-center flex items-center justify-center px-4 py-3 text-lg border border-solid rounded-[4px] block`;
  const solid = `${
    btnClr === "secondary"
      ? "bg-secondary text-[#313131] border-secondary hover:bg-transparent hover:text-secondary"
      : "bg-primary text-white border-primary hover:bg-transparent hover:text-primary hover:border hover:border-solid hover:border-primary"
  }`;
  const outline = `${
    btnClr === "secondary"
      ? "bg-transparent text-secondary border-secondary hover:bg-secondary/5"
      : "bg-transparent text-primary border-primary hover:bg-primary/5"
  }`;

  if (btnType === "outline") {
    return (
      <Link
        className={`transition-colors ease-linear duration-150 ${cls} ${outline}`}
        to={slug(btnLink)}
      >
        {btnText}
      </Link>
    );
  }
  return (
    <Link
      className={`transition-colors ease-linear duration-150 ${cls} ${solid}`}
      to={slug(btnLink)}
    >
      {btnText}
    </Link>
  );
};

export default Btn;

const normalizeExternalUrl = (url) => {
  if (typeof url !== "string") return "";
  const trimmed = url.trim();
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

/** Maps known domains to friendly readable names for screen readers. */
const SITE_NAMES = {
  "order.simplysalad.com": "Simply Salad ordering",
  "doordash.com": "DoorDash",
  "ubereats.com": "Uber Eats",
  "grubhub.com": "Grubhub",
  "apps.apple.com": "Apple App Store",
  "itunes.apple.com": "Apple App Store",
  "play.google.com": "Google Play Store",
  "instagram.com": "Instagram",
  "facebook.com": "Facebook",
  "twitter.com": "Twitter",
  "x.com": "X",
  "yelp.com": "Yelp",
  "bit.ly": "bit dot ly",
  "tinyurl.com": "tinyurl dot com",
};

/** Returns a friendly site name for a URL, falling back to the bare hostname. */
const getSiteName = (url) => {
  try {
    const parsedUrl = new URL(normalizeExternalUrl(url));
    const hostname = parsedUrl.hostname.replace(/^www\./, "");

    if (hostname === "bit.ly" || hostname === "tinyurl.com") {
      const shortSlug = parsedUrl.pathname
        .replace(/^\/+/, "")
        .split("/")[0]
        ?.replace(/[^a-zA-Z0-9]/g, "")
        ?.toLowerCase();
      if (shortSlug) return shortSlug;
    }

    // Exact match first, then suffix match
    if (SITE_NAMES[hostname]) return SITE_NAMES[hostname];

    const suffixMatch = Object.entries(SITE_NAMES).find(
      ([domain]) => hostname === domain || hostname.endsWith(`.${domain}`)
    )?.[1];

    if (suffixMatch) return suffixMatch;

    return `the ${hostname} website`;
  } catch {
    return null;
  }
};

const isExternalUrl = (url) => {
  try {
    const hostname = new URL(normalizeExternalUrl(url)).hostname.replace(
      /^www\./,
      ""
    );
    return hostname !== "order.simplysalad.com";
  } catch {
    return true;
  }
};

const getSupplementalText = (buttonText, data) => {
  if (data.linkType === "slug") {
    const readableSlug = data.slug?.current?.replace(/-/g, " ");
    return readableSlug && readableSlug !== buttonText
      ? `, navigates to ${readableSlug}`
      : "";
  }

  if (data.linkType === "url") {
    if (isExternalUrl(data.url)) {
      const siteName = getSiteName(data.url);
      return siteName
        ? `, redirects to off-site ${siteName}`
        : ", redirects to off-site link";
    }
    return data.url ? ", opens Simply Salad ordering" : "";
  }

  if (data.linkType === "upload") {
    return "";
  }

  return "";
};

const SupplementalText = ({ buttonText, data }) => {
  if (data.linkType === "upload") {
    return <span className="normal-case"> (PDF)</span>;
  }

  const supplementalText = getSupplementalText(buttonText, data);
  if (!supplementalText) return null;

  return <span className="sr-only">{supplementalText}</span>;
};

export const Btn2 = ({ data, cls = "" }) => {
  const className = `min-w-[167px] h-10 text-center flex items-center justify-center px-4 py-2 text-lg border border-solid font-bold rounded font-nunito`;
  const solid = `${
    data.buttonColor === "secondary"
      ? "bg-secondary-gradient border-secondary group"
      : "bg-primary-gradient border-primary hover:border hover:border-solid hover:border-primary group"
  }`;

  const text = `${
    data.buttonColor === "secondary"
      ? "text-white group-hover:text-secondary"
      : "text-white group-hover:text-primary"
  }`;
  const outline = `${
    data.buttonColor === "secondary"
      ? "bg-transparent text-secondary !border-2 !border-solid !border-secondary hover:bg-secondary hover:text-white no-underline hover:no-underline"
      : "bg-transparent text-primary !border-2 !border-solid !border-primary hover:bg-primary hover:text-white no-underline hover:no-underline"
  }`;

  const underline = `${
    data.buttonColor === "secondary"
      ? "bg-transparent text-secondary border-0 underline underline-offset-4"
      : "bg-transparent border-0 text-primary underline underline-offset-4"
  }`;

  if (data.buttonType === "outline") {
    if (data.linkType === "slug") {
      return (
        <Link
          className={twMerge(
            `transition-colors ease-linear duration-150 ${className} ${outline}`,
            cls
          )}
          to={slug(data.slug.current)}
        >
          {data.buttonText}
          <SupplementalText buttonText={data.buttonText} data={data} />
        </Link>
      );
    } else if (data.linkType === "url") {
      return (
        <a
          className={twMerge(
            `transition-colors ease-linear duration-150 ${className} ${outline}`,
            cls
          )}
          href={data.url}
          target={
            data.url?.includes("order.simplysalad.com") ? "_self" : "_blank"
          }
          rel="noopener noreferrer"
        >
          {data.buttonText}
          <SupplementalText buttonText={data.buttonText} data={data} />
        </a>
      );
    } else if (data.linkType === "upload") {
      return (
        <a
          className={twMerge(
            `transition-colors ease-linear duration-150 ${className} ${outline}`,
            cls
          )}
          href={data.pdf.asset.url}
          target={
            data.pdf.asset.url?.includes("order.simplysalad.com")
              ? "_self"
              : "_blank"
          }
          rel="noopener noreferrer"
        >
          {data.buttonText}
          <SupplementalText buttonText={data.buttonText} data={data} />
        </a>
      );
    }
  } else if (data.buttonType === "none") {
    if (data.linkType === "slug") {
      return (
        <Link
          className={twMerge(
            `transition-colors ease-linear duration-150 ${className} ${underline}`,
            `${cls} min-w-20`
          )}
          to={slug(data.slug.current)}
        >
          {data.buttonText}
          <SupplementalText buttonText={data.buttonText} data={data} />
        </Link>
      );
    } else if (data.linkType === "url") {
      return (
        <a
          className={twMerge(
            `transition-colors ease-linear duration-150 ${className} ${underline}`,
            `${cls} min-w-20`
          )}
          href={data.url}
          target={
            data.url?.includes("order.simplysalad.com") ? "_self" : "_blank"
          }
          rel="noopener noreferrer"
        >
          {data.buttonText}
          <SupplementalText buttonText={data.buttonText} data={data} />
        </a>
      );
    } else if (data.linkType === "upload") {
      return (
        <a
          className={twMerge(
            `transition-colors ease-linear duration-150 ${className} ${underline}`,
            `${cls} min-w-20`
          )}
          href={data.pdf.asset.url}
          target={
            data.pdf.asset.url?.includes("order.simplysalad.com")
              ? "_self"
              : "_blank"
          }
          rel="noopener noreferrer"
        >
          {data.buttonText}
          <SupplementalText buttonText={data.buttonText} data={data} />
        </a>
      );
    }
  } else {
    if (data.linkType === "slug") {
      return (
        <Link
          className={twMerge(
            `transition-colors ease-linear duration-150 ${className} ${solid}`,
            cls
          )}
          to={slug(data.slug.current)}
        >
          <span className={text}>{data.buttonText}</span>
          <SupplementalText buttonText={data.buttonText} data={data} />
        </Link>
      );
    } else if (data.linkType === "url") {
      return (
        <a
          className={twMerge(
            `transition-colors ease-linear duration-150 ${className} ${solid}`,
            cls
          )}
          href={data.url}
          target={
            data.url?.includes("order.simplysalad.com") ? "_self" : "_blank"
          }
          rel="noopener noreferrer"
        >
          <span className={text}>{data.buttonText}</span>
          <SupplementalText buttonText={data.buttonText} data={data} />
        </a>
      );
    } else if (data.linkType === "upload") {
      return (
        <a
          className={twMerge(
            `transition-colors ease-linear duration-150 ${className} ${solid}`,
            cls
          )}
          href={data.pdf.asset.url}
          target={
            data.pdf.asset.url?.includes("order.simplysalad.com")
              ? "_self"
              : "_blank"
          }
          rel="noopener noreferrer"
        >
          <span className={text}>{data.buttonText}</span>
          <SupplementalText buttonText={data.buttonText} data={data} />
        </a>
      );
    }
  }
};
